/* global safeArray, num, isoToNb, isPostedPayrollRun */
// LØNNSAVSTEMMING — seksjonene under hovedavstemmingen
//
// Skilt fra backend-payroll-recon-view.jsx, som eier hovedavstemmingen og
// kontrollkontoene. Her ligger utledningene hver enkelt seksjon i skjermen
// spør om: kjøringene, A-meldingen, bankutbetalingene, skatt og
// arbeidsgiveravgift, og feriepenger og pensjon.
//
// Alle leser lønnskjøringenes egne tall. Ingen av dem regner lønn på nytt.

// Lønnskjøringene i perioden, med de tre statusene avstemmingen spør om:
// er den bokført, er den meldt, og er den betalt.
const payReconRunRows = ({ runs, lineTotals, range }) => runs.map((run) => {
  const totals = safeArray(run.lines).reduce((sum, line) => {
    const row = lineTotals(line);
    return {
      gross: sum.gross + row.gross, tax: sum.tax + row.tax,
      net: sum.net + row.net, aga: sum.aga + row.aga,
    };
  }, { gross: 0, tax: 0, net: 0, aga: 0 });
  const posted = isPostedPayrollRun(run);
  const paidAt = String(run?.paymentStatus?.paidAt || run?.paidAt || '').slice(0, 10);
  const paidAfterPeriod = Boolean(paidAt) && paidAt > range.to;
  const status = !posted ? 'utkast'
    : !run.altinnSent ? 'amelding_mangler'
      : !paidAt ? 'ikke_utbetalt'
        : paidAfterPeriod ? 'utbetalt_etter_periode'
          : 'fullfort';
  return {
    id: run.id,
    label: run.period ? t('pay.recon.runLabelPeriod', { period: run.period }) : t('pay.recon.runLabelRun', { date: isoToNb(run.paymentDate) }),
    period: run.period || '',
    paymentDate: isoToNb(run.paymentDate),
    employees: safeArray(run.lines).length,
    gross: Math.round(totals.gross),
    tax: Math.round(totals.tax),
    net: Math.round(totals.net),
    aga: Math.round(totals.aga),
    voucherNumber: run.voucherNumber || '',
    posted,
    reported: Boolean(run.altinnSent),
    reportedAt: run.altinnSubmittedAt ? isoToNb(String(run.altinnSubmittedAt).slice(0, 10)) : '',
    reportedMode: run.altinnSubmissionMode || '',
    paid: Boolean(paidAt),
    paidDate: paidAt ? isoToNb(paidAt) : '',
    paidAfterPeriod,
    status,
  };
});

// A-meldingen. Plumme sender den ikke selv i dag — den registreres manuelt når
// den er levert utenfor appen (`altinnSent` / `altinnSubmissionMode`). Derfor
// kan avstemmingen bekrefte at en kjøring ER meldt, og hva den i så fall ble
// meldt med, men ikke hva Altinn faktisk mottok. Å vise et «rapportert» tall
// som egentlig er lønnssystemets eget ville sett ut som en kontroll og ikke
// vært det, så seksjonen sier det i klartekst i stedet.
const payReconAmelding = ({ runRows, systemTotals, employeeCount }) => {
  const reported = runRows.filter((row) => row.reported);
  const missing = runRows.filter((row) => row.posted && !row.reported);
  return {
    submitted: reported.length,
    postedRuns: runRows.filter((row) => row.posted).length,
    missingRuns: missing,
    complete: missing.length === 0 && reported.length > 0,
    lastSubmittedAt: reported.map((row) => row.reportedAt).filter(Boolean).sort().pop() || '',
    manualOnly: reported.every((row) => row.reportedMode === 'manual'),
    employees: employeeCount,
    gross: Math.round(systemTotals.gross || 0),
    tax: Math.round(systemTotals.tax || 0),
    agaBase: Math.round(systemTotals.agaBase || 0),
    aga: Math.round(systemTotals.aga || 0),
  };
};

// Bankutbetalingene. Appen kobler ikke hver ansatt til hver banklinje — netto
// lønn betales som en utbetaling per kjøring, og det er den koblingen som
// finnes (`markPayrollRunPaid`). Seksjonen sammenlikner derfor forventet netto
// lønn med det som er registrert utbetalt, per kjøring.
const payReconBank = ({ runRows, range }) => {
  const posted = runRows.filter((row) => row.posted);
  const expected = posted.reduce((sum, row) => sum + row.net, 0);
  const paid = posted.filter((row) => row.paid).reduce((sum, row) => sum + row.net, 0);
  const rows = posted.map((row) => ({
    id: row.id,
    label: row.label,
    employees: row.employees,
    expected: row.net,
    paid: row.paid ? row.net : 0,
    paidDate: row.paidDate,
    difference: row.paid ? 0 : row.net,
    flag: !row.paid ? 'ikke_utbetalt' : row.paidAfterPeriod ? 'etter_periodedato' : '',
    flagLabel: !row.paid ? t('pay.recon.notPaid') : row.paidAfterPeriod ? t('pay.recon.paidAfterPeriod') : '',
  }));
  return {
    expected: Math.round(expected),
    paid: Math.round(paid),
    difference: Math.round(expected - paid),
    unpaidRuns: rows.filter((row) => row.flag === 'ikke_utbetalt').length,
    lateRuns: rows.filter((row) => row.flag === 'etter_periodedato').length,
    periodEnd: range.to,
    rows,
  };
};

// Skatt og arbeidsgiveravgift. Betalingsstatusen er terminmodellens egen
// (`derivePayroll().terms`) — avstemmingen finner ikke ut på nytt hva som er
// betalt, den leser hva betalingene sier.
const payReconTaxSection = ({ systemTotals, terms, range }) => {
  const relevant = safeArray(terms).filter((term) => {
    const due = String(term.dueIso || '').slice(0, 10);
    return !due || due >= range.from;
  });
  const open = relevant.filter((term) => term.status !== 'betalt');
  return {
    tax: Math.round(systemTotals.tax || 0),
    aga: Math.round(systemTotals.aga || 0),
    agaBase: Math.round(systemTotals.agaBase || 0),
    terms: relevant,
    openTerms: open.length,
    openAmount: Math.round(open.reduce((sum, term) => sum + num(term.open), 0)),
  };
};

// Feriepenger og pensjon. Feriepengeforpliktelsen er inngående saldo på
// feriepengekontoen pluss det som er opptjent i perioden, minus det som er
// utbetalt — mot det hovedboken faktisk står med.
const payReconHolidayPension = ({ systemTotals, controlAccounts, settings }) => {
  const holidayAccount = controlAccounts.find((account) => account.role === 'feriepenger');
  const agaHolidayAccount = controlAccounts.find((account) => account.role === 'aga_feriepenger');
  const pensionAccount = controlAccounts.find((account) => account.role === 'pensjon');
  const opening = holidayAccount ? holidayAccount.openingBalance : null;
  const earned = Math.round(systemTotals.holidayAccrual || 0);
  const paid = Math.round(systemTotals.holidayPayout || 0);
  const computed = opening === null ? null : Math.round(Math.abs(opening) + earned - paid);
  const booked = holidayAccount ? Math.round(Math.abs(holidayAccount.bookedBalance)) : null;
  const rate = num(settings.employerContributionRate, 14.1);
  return {
    opening: opening === null ? null : Math.round(Math.abs(opening)),
    earned,
    paid,
    computed,
    booked,
    difference: computed === null || booked === null ? null : computed - booked,
    holidayBase: Math.round(systemTotals.holidayBase || 0),
    agaRate: rate,
    agaComputed: Math.round(earned * rate / 100),
    agaBooked: agaHolidayAccount ? Math.round(Math.abs(agaHolidayAccount.movement)) : null,
    pensionComputed: Math.round(systemTotals.pension || 0),
    pensionBooked: pensionAccount ? Math.round(Math.abs(pensionAccount.movement)) : null,
    hasHolidayAccount: Boolean(holidayAccount),
    hasPensionAccount: Boolean(pensionAccount),
  };
};


// ------------------------------------------------------------------
// Varslene på en lønnslinje
// ------------------------------------------------------------------
// Hvert varsel peker på noe dataene faktisk sier. Ingen av dem er en
// konklusjon: «mulig dobbeltutbetaling» betyr at to linjer ser like ut, ikke at
// noen er betalt to ganger. Konklusjonen er byråets, og lagres som vurderingen.

// Varselkodene er stabile — de brukes til filtrering og lagring. Etiketten er
// oversatt og lest ut ved bruk (ikke frosset ved modul-innlasting), slik at et
// senere språkbytte oppdaterer teksten. `PAY_RECON_FLAG` beholder sin
// indekserbare form (`PAY_RECON_FLAG[flag]`) for eksisterende forbrukere.
const payReconFlagLabel = (flag) => t(`pay.recon.flag.${flag}`, String(flag || ''));
const PAY_RECON_FLAG = new Proxy({}, {
  get: (target, prop) => (typeof prop === 'string' ? payReconFlagLabel(prop) : undefined),
});

// Terskelen for «unormal» er en arbeidsterskel, ikke en regel: 30 % avvik fra
// det den ansatte har hatt tidligere i året er verdt et blikk. Uten historikk
// finnes ingen endring å måle, og da settes ikke varselet.
const PAY_RECON_CHANGE_LIMIT = 0.3;

const payReconTaxCardValid = (employee, referenceDate) => {
  if (!employee) return true;
  if (String(employee.taxCardStatus || '') !== 'fetched') return false;
  const validTo = String(employee.taxCardValidTo || '');
  return !validTo || validTo >= String(referenceDate || '');
};

const payReconEmployeeItems = ({ runs, state, range, lineTotals }) => {
  const employees = new Map(safeArray(state.employees).map((employee) => [String(employee?.id), employee]));
  // Historikken er kjøringene FØR denne perioden. Sammenlikner man med
  // perioden selv, måler man linjen mot seg selv.
  const history = new Map();
  for (const run of safeArray(state.payrollRuns)) {
    if (String(run?.period || '').slice(0, 7) >= String(range.from).slice(0, 7)) continue;
    if (!isPostedPayrollRun(run)) continue;
    for (const line of safeArray(run.lines)) {
      const key = String(line?.employeeId || '');
      const entry = history.get(key) || { sum: 0, count: 0 };
      entry.sum += num(line?.grossSalary);
      entry.count += 1;
      history.set(key, entry);
    }
  }

  // Duplikatnøkkelen er samme ansatt og samme bruttobeløp i samme periode. To
  // like beløp kan være helt reelle — en bonuskjøring i tillegg til lønn — så
  // dette er et forslag til gjennomgang.
  const duplicateKeys = new Map();
  for (const run of runs) {
    for (const line of safeArray(run.lines)) {
      const key = `${line?.employeeId || ''}|${num(line?.grossSalary).toFixed(2)}`;
      duplicateKeys.set(key, (duplicateKeys.get(key) || 0) + 1);
    }
  }

  const items = [];
  for (const run of runs) {
    const paymentDate = String(run?.paymentDate || '').slice(0, 10) || range.to;
    for (const line of safeArray(run.lines)) {
      const employee = employees.get(String(line?.employeeId || ''));
      const totals = lineTotals(line);
      const previous = history.get(String(line?.employeeId || ''));
      const average = previous && previous.count ? previous.sum / previous.count : null;
      const change = average && average > 0 ? (totals.gross - average) / average : null;
      const endDate = String(employee?.employmentEndDate || '').slice(0, 10);

      const flags = [];
      if (totals.gross > 0 && totals.net <= 0) flags.push('negativ_netto');
      if (totals.gross > 0 && !payReconTaxCardValid(employee, paymentDate)) flags.push('mangler_skattekort');
      if (endDate && endDate < paymentDate) flags.push('sluttet_ansatt');
      if (totals.benefits > 0) flags.push('naturalytelse');
      if (change !== null && Math.abs(change) > PAY_RECON_CHANGE_LIMIT) flags.push('uvanlig_endring');
      if (!run?.altinnSent) flags.push('mangler_amelding');
      if (duplicateKeys.get(`${line?.employeeId || ''}|${num(line?.grossSalary).toFixed(2)}`) > 1) flags.push('mulig_duplikat');
      if (isPostedPayrollRun(run) && !run?.paymentStatus?.paidAt && !run?.paidAt) flags.push('ikke_utbetalt');

      items.push({
        id: `${run.id}-${line.id}`,
        runId: run.id,
        runLabel: run.period ? t('pay.recon.runLabelPeriod', { period: run.period }) : t('pay.recon.runLabelRun', { date: isoToNb(run.paymentDate) }),
        employeeId: line.employeeId || '',
        employeeName: line.employeeName || employee?.name || t('pay.recon.unknownEmployee'),
        employmentType: employee?.payType === 'hourly' ? t('pay.hourlyPay') : endDate && endDate < paymentDate ? t('pay.employmentStatusLabel.ended') : t('pay.recon.employmentTypeFixed'),
        paymentDate: isoToNb(run.paymentDate),
        gross: payReconRound(totals.gross),
        net: payReconRound(totals.net),
        tax: payReconRound(totals.tax),
        benefits: payReconRound(totals.benefits),
        holidayAccrual: payReconRound(totals.holidayAccrual),
        changePct: change === null ? null : Math.round(change * 100),
        ameldingLabel: run?.altinnSent ? t('pay.recon.reported') : t('pay.recon.notReported'),
        bankLabel: run?.paymentStatus?.paidAt || run?.paidAt
          ? (String(run.paymentStatus?.paidAt || run.paidAt).slice(0, 10) > range.to ? t('pay.recon.afterPeriodDate') : t('pay.paidOut'))
          : t('pay.recon.notPaid'),
        flags,
      });
    }
  }
  return items;
};


Object.assign(window, {
  payReconRunRows, payReconAmelding, payReconBank, payReconTaxSection, payReconHolidayPension,
  payReconEmployeeItems, PAY_RECON_FLAG, payReconFlagLabel,
});
